import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Tabs, Tab } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import TaskApprovalStageEditor from "./TaskApprovalStageEditor";

// Phase 4B, §17/§18/§26. Create/edit a policy's own configuration + its
// ordered stage list. Stages are saved via the transactional-replacement
// PUT .../stages endpoint, separately from the policy's own fields (§18).
const TRIGGER_TYPES = ["MANUAL_SUBMISSION", "BEFORE_TRANSITION", "ON_STATUS_ENTRY"];
const REJECTION_BEHAVIOURS = ["KEEP_CURRENT_STATUS", "MOVE_TO_STATUS", "CANCEL_PENDING_TRANSITION"];
const CHANGES_REQUESTED_BEHAVIOURS = ["KEEP_CURRENT_STATUS", "REOPEN_TASK", "MOVE_TO_STATUS"];

const initialForm = {
  code: "", name: "", description: "",
  task_type_id: "", department_id: "", project_id: "", project_module_id: "", priority: "",
  trigger_type: "MANUAL_SUBMISSION", trigger_transition_id: "", trigger_status_id: "", completion_transition_id: "",
  rejection_behaviour: "KEEP_CURRENT_STATUS", rejection_target_status_id: "",
  changes_requested_behaviour: "KEEP_CURRENT_STATUS", changes_requested_target_status_id: "",
  allow_requester_cancel: true, allow_approver_delegate: true, is_required: true, priority_order: 0,
};

export default function TaskApprovalPolicyEditor({ show, onHide, policy, taskTypes, departments, projects, users, roles, taskStatuses, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [stages, setStages] = useState([]);
  const [projectModules, setProjectModules] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState("");
  const [transitions, setTransitions] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEditing = !!policy;
  const isUsed = !!policy?.usage_count;

  useEffect(() => {
    if (!show) return;
    if (policy) {
      setForm({
        code: policy.code || "", name: policy.name || "", description: policy.description || "",
        task_type_id: policy.task_type_id || "", department_id: policy.department_id || "",
        project_id: policy.project_id || "", project_module_id: policy.project_module_id || "", priority: policy.priority || "",
        trigger_type: policy.trigger_type || "MANUAL_SUBMISSION",
        trigger_transition_id: policy.trigger_transition_id || "", trigger_status_id: policy.trigger_status_id || "",
        completion_transition_id: policy.completion_transition_id || "",
        rejection_behaviour: policy.rejection_behaviour || "KEEP_CURRENT_STATUS", rejection_target_status_id: policy.rejection_target_status_id || "",
        changes_requested_behaviour: policy.changes_requested_behaviour || "KEEP_CURRENT_STATUS", changes_requested_target_status_id: policy.changes_requested_target_status_id || "",
        allow_requester_cancel: !!policy.allow_requester_cancel, allow_approver_delegate: !!policy.allow_approver_delegate,
        is_required: !!policy.is_required, priority_order: policy.priority_order || 0,
      });
      setStages((policy.stages || []).map((s) => ({ ...s })));
      if (policy.project_id) fetchModules(policy.project_id);
    } else {
      setForm(initialForm);
      setStages([]);
    }
    api.get("/task-workflows", { params: { is_active: 1, pageSize: 200 } }).then((res) => setWorkflows(Array.isArray(res.data?.data) ? res.data.data : [])).catch(() => setWorkflows([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, policy]);

  const fetchModules = async (projectId) => {
    if (!projectId) { setProjectModules([]); return; }
    try {
      const res = await api.get(`/projects/${projectId}/modules`, { params: { is_active: 1, pageSize: 200 } });
      setProjectModules(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { setProjectModules([]); }
  };

  const fetchTransitions = async (workflowId) => {
    setSelectedWorkflowId(workflowId);
    if (!workflowId) { setTransitions([]); return; }
    try {
      const res = await api.get(`/task-workflows/${workflowId}`);
      setTransitions(Array.isArray(res.data?.data?.transitions) ? res.data.data.transitions : []);
    } catch { setTransitions([]); }
  };

  const savePolicy = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.warning("code and name are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      let savedPolicy;
      if (isEditing) {
        const res = await api.put(`/task-approval-policies/${policy.id}`, payload);
        savedPolicy = res.data?.data;
        toast.success("Policy updated");
      } else {
        const res = await api.post("/task-approval-policies", payload);
        savedPolicy = res.data?.data;
        toast.success("Policy created — configure stages, then activate.");
      }
      if (stages.length) {
        await api.put(`/task-approval-policies/${savedPolicy.id}/stages`, {
          stages: stages.map((s) => ({
            ...s,
            fixed_user_id: s.fixed_user_id || null,
            role_id: s.role_id || null,
            project_membership_type: s.project_membership_type || null,
          })),
        });
      }
      onSaved?.();
      onHide();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TASK_APPROVAL_POLICY_IN_USE") toast.error("This policy's code cannot be changed once it has been used — deactivate and create a new policy instead.");
      else toast.error(data?.message || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton><Modal.Title>{isEditing ? `Edit Policy — ${policy.name}` : "New Approval Policy"}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Tabs defaultActiveKey="general" className="mb-3">
          <Tab eventKey="general" title="General">
            <Row className="g-2">
              <Col md={4}>
                <Form.Label className="small">Code</Form.Label>
                <Form.Control size="sm" disabled={isUsed} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} />
                {isUsed && <div className="text-muted small">Code is immutable once this policy has been used.</div>}
              </Col>
              <Col md={8}>
                <Form.Label className="small">Name</Form.Label>
                <Form.Control size="sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Col>
              <Col md={12}>
                <Form.Label className="small">Description</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="scope" title="Scope">
            <Row className="g-2">
              <Col md={3}>
                <Form.Label className="small">Task Type (optional)</Form.Label>
                <Form.Select size="sm" value={form.task_type_id} onChange={(e) => setForm({ ...form, task_type_id: e.target.value })}>
                  <option value="">Any</option>
                  {(taskTypes || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Department (optional)</Form.Label>
                <Form.Select size="sm" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                  <option value="">Any</option>
                  {(departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Project (optional)</Form.Label>
                <Form.Select size="sm" value={form.project_id} onChange={(e) => { setForm({ ...form, project_id: e.target.value, project_module_id: "" }); fetchModules(e.target.value); }}>
                  <option value="">Any</option>
                  {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Module (optional)</Form.Label>
                <Form.Select size="sm" value={form.project_module_id} onChange={(e) => setForm({ ...form, project_module_id: e.target.value })} disabled={!form.project_id}>
                  <option value="">Any</option>
                  {projectModules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Priority (optional)</Form.Label>
                <Form.Select size="sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="">Any</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Resolution Priority Order</Form.Label>
                <Form.Control size="sm" type="number" value={form.priority_order} onChange={(e) => setForm({ ...form, priority_order: Number(e.target.value) || 0 })} />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="trigger" title="Trigger &amp; Completion">
            <Row className="g-2">
              <Col md={4}>
                <Form.Label className="small">Trigger Type</Form.Label>
                <Form.Select size="sm" value={form.trigger_type} onChange={(e) => setForm({ ...form, trigger_type: e.target.value })}>
                  {TRIGGER_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </Form.Select>
              </Col>
              {(form.trigger_type === "BEFORE_TRANSITION" || form.trigger_type !== "MANUAL_SUBMISSION") && (
                <Col md={4}>
                  <Form.Label className="small">Workflow (to pick a transition from)</Form.Label>
                  <Form.Select size="sm" value={selectedWorkflowId} onChange={(e) => fetchTransitions(e.target.value)}>
                    <option value="">Select...</option>
                    {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </Form.Select>
                </Col>
              )}
              {form.trigger_type === "BEFORE_TRANSITION" && (
                <Col md={4}>
                  <Form.Label className="small">Trigger Transition</Form.Label>
                  <Form.Select size="sm" value={form.trigger_transition_id} onChange={(e) => setForm({ ...form, trigger_transition_id: e.target.value })}>
                    <option value="">Select...</option>
                    {transitions.map((t) => <option key={t.id} value={t.id}>{t.transition_name}</option>)}
                  </Form.Select>
                </Col>
              )}
              {form.trigger_type === "ON_STATUS_ENTRY" && (
                <Col md={4}>
                  <Form.Label className="small">Trigger Status</Form.Label>
                  <Form.Select size="sm" value={form.trigger_status_id} onChange={(e) => setForm({ ...form, trigger_status_id: e.target.value })}>
                    <option value="">Select...</option>
                    {(taskStatuses || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Form.Select>
                </Col>
              )}
              <Col md={4}>
                <Form.Label className="small">Completion Transition (executed once approved)</Form.Label>
                <Form.Select size="sm" value={form.completion_transition_id} onChange={(e) => setForm({ ...form, completion_transition_id: e.target.value })}>
                  <option value="">None</option>
                  {transitions.map((t) => <option key={t.id} value={t.id}>{t.transition_name}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="behaviour" title="Reject / Changes Requested">
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="small">Rejection Behaviour</Form.Label>
                <Form.Select size="sm" value={form.rejection_behaviour} onChange={(e) => setForm({ ...form, rejection_behaviour: e.target.value })}>
                  {REJECTION_BEHAVIOURS.map((b) => <option key={b} value={b}>{b.replace(/_/g, " ")}</option>)}
                </Form.Select>
              </Col>
              {form.rejection_behaviour === "MOVE_TO_STATUS" && (
                <Col md={6}>
                  <Form.Label className="small">Rejection Target Status</Form.Label>
                  <Form.Select size="sm" value={form.rejection_target_status_id} onChange={(e) => setForm({ ...form, rejection_target_status_id: e.target.value })}>
                    <option value="">Select...</option>
                    {(taskStatuses || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Form.Select>
                </Col>
              )}
              <Col md={6}>
                <Form.Label className="small">Changes-Requested Behaviour</Form.Label>
                <Form.Select size="sm" value={form.changes_requested_behaviour} onChange={(e) => setForm({ ...form, changes_requested_behaviour: e.target.value })}>
                  {CHANGES_REQUESTED_BEHAVIOURS.map((b) => <option key={b} value={b}>{b.replace(/_/g, " ")}</option>)}
                </Form.Select>
              </Col>
              {form.changes_requested_behaviour === "MOVE_TO_STATUS" && (
                <Col md={6}>
                  <Form.Label className="small">Changes-Requested Target Status</Form.Label>
                  <Form.Select size="sm" value={form.changes_requested_target_status_id} onChange={(e) => setForm({ ...form, changes_requested_target_status_id: e.target.value })}>
                    <option value="">Select...</option>
                    {(taskStatuses || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </Form.Select>
                </Col>
              )}
              <Col md={4}>
                <Form.Check type="checkbox" label="Allow requester to cancel" checked={form.allow_requester_cancel} onChange={(e) => setForm({ ...form, allow_requester_cancel: e.target.checked })} />
              </Col>
              <Col md={4}>
                <Form.Check type="checkbox" label="Allow approver to delegate a stage" checked={form.allow_approver_delegate} onChange={(e) => setForm({ ...form, allow_approver_delegate: e.target.checked })} />
              </Col>
              <Col md={4}>
                <Form.Check type="checkbox" label="Approval required when this policy matches" checked={form.is_required} onChange={(e) => setForm({ ...form, is_required: e.target.checked })} />
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="stages" title="Stages">
            <TaskApprovalStageEditor stages={stages} onChange={setStages} users={users} roles={roles} />
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" disabled={saving} onClick={savePolicy}>{saving ? "Saving..." : "Save Policy"}</Button>
      </Modal.Footer>
    </Modal>
  );
}
